import { BodyText, Icon } from '@postscript/components';
import { getSystemMessageIcon } from 'components/responses/utils/messages';
import { isBubAI } from 'components/sales/pages/utils';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

interface GroupedProfileEventProps {
  messages: Array<{
    text: string;
    direction?: string;
    eventData?: {
      description?: string;
      // eslint-disable-next-line camelcase
      public_metadata?: string;
      [key: string]: any;
    };
    // eslint-disable-next-line camelcase
    created_at?: string;
    [key: string]: any;
  }>;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  background: transparent;
  padding: 8px 0;
  gap: 8px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color-dim);
  min-width: 20px;
  max-width: 20px;
  height: 20px;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const MainContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const HeaderTimestamp = styled.div`
  margin-left: auto;
  white-space: nowrap;
`;

const GroupedEventsContainer = styled.div`
  margin-top: 4px;
  padding: 0 0 0 28px;
  background: transparent;
`;

const GroupedEventItem = styled.div`
  padding: 2px 0;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const GroupedEventContent = styled.div`
  flex: 1;
`;

const GroupedEventTime = styled.div`
  margin-left: auto;
  white-space: nowrap;
`;

const GroupedProfileEvent: React.FC<GroupedProfileEventProps> = ({
  messages,
}) => {
  const { isLive } = useShopperStatus();
  const { hasLabsFlag } = usePSLabs();
  const isSender = !!(isLive && hasLabsFlag('ai_managed_outbound'));
  const [expanded, setExpanded] = useState(false);
  const uniqueId = useMemo(
    () => `grouped-profile-event-${Math.random().toString(36).substr(2, 9)}`,
    [],
  );

  if (!isBubAI() && !isSender) {
    return null;
  }

  if (!messages || messages.length === 0) {
    return null;
  }

  const formatTimestamp = (timestamp: any): string | null => {
    try {
      // Check if timestamp is already a Moment instance
      if (
        timestamp &&
        typeof timestamp === 'object' &&
        typeof timestamp.isValid === 'function' &&
        typeof timestamp.format === 'function'
      ) {
        return timestamp.isValid()
          ? timestamp.format('MM/DD/YY • h:mm A')
          : null;
      }

      // Fall back to parsing strings
      const parsedMoment = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
      return parsedMoment.isValid()
        ? parsedMoment.format('MM/DD/YY • h:mm A')
        : null;
    } catch (error) {
      console.warn(`Error parsing timestamp:`, timestamp, error);
      return null;
    }
  };

  const formatTimeOnly = (timestamp: any): string | null => {
    try {
      // Check if timestamp is already a Moment instance
      if (
        timestamp &&
        typeof timestamp === 'object' &&
        typeof timestamp.isValid === 'function' &&
        typeof timestamp.format === 'function'
      ) {
        return timestamp.isValid() ? timestamp.format('h:mm A') : null;
      }

      // Fall back to parsing strings
      const parsedMoment = moment(timestamp, 'YYYY-MM-DD HH:mm:ss', true);
      return parsedMoment.isValid() ? parsedMoment.format('h:mm A') : null;
    } catch (error) {
      console.warn(`Error parsing timestamp:`, timestamp, error);
      return null;
    }
  };

  const firstMessage = messages[0];
  const eventType = firstMessage.eventData?.description || firstMessage.text;
  const firstTimestamp = firstMessage.createdAt
    ? formatTimestamp(firstMessage.createdAt)
    : null;

  const mainText = `${eventType} (${messages.length} items)`;
  const resolvedIcon = getSystemMessageIcon(eventType);

  return (
    <>
      <button
        type="button"
        style={{
          background: 'none',
          border: 'none',
          width: '100%',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          display: 'block',
          textAlign: 'inherit',
        }}
        aria-expanded={expanded}
        aria-controls={uniqueId}
        onClick={() => setExpanded((prev) => !prev)}
        tabIndex={0}
      >
        <Container>
          <IconWrapper>
            <Icon
              color="var(--text-color-dim)"
              component={resolvedIcon}
              size={16}
            />
          </IconWrapper>
          <ContentWrapper>
            <MainContent>
              <BodyText
                color="var(--text-color)"
                size="x-small"
                style={{ fontWeight: 500 }}
              >
                {mainText}
              </BodyText>
            </MainContent>
            {firstTimestamp && (
              <HeaderTimestamp>
                <BodyText color="var(--text-color-dim)" size="x-small">
                  {firstTimestamp}
                </BodyText>
              </HeaderTimestamp>
            )}
          </ContentWrapper>
          <span
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            >
              <path
                d="M6 8L10 12L14 8"
                stroke="var(--text-color-dim)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </Container>
      </button>
      {expanded && (
        <div
          id={uniqueId}
          style={{
            background: 'transparent',
            marginTop: '4px',
            padding: '0',
          }}
        >
          <GroupedEventsContainer>
            {messages.map((message, index) => {
              const { description, public_metadata: publicMetadata } =
                message.eventData || {};
              const timeOnly = message.createdAt
                ? formatTimeOnly(message.createdAt)
                : null;

              // Remove redundant prefix text - just show the metadata or specific item info
              let eventContent = publicMetadata || description || message.text;

              // If we have publicMetadata, that's the specific item info we want to show
              // If not, show the description but clean up any redundant text
              if (!publicMetadata && description) {
                // Remove common prefixes like "Subscriber browsed:" etc.
                eventContent = description.replace(
                  /^(Subscriber\s+)?(browsed|added to cart|purchased):\s*/i,
                  '',
                );
              }

              // Create a unique key using message properties instead of index
              const messageKey = `${message.key || message.text}-${
                timeOnly || index
              }`;

              return (
                <GroupedEventItem key={messageKey}>
                  <GroupedEventContent>
                    <BodyText
                      color="var(--text-color)"
                      size="x-small"
                      style={{ fontWeight: 600 }}
                    >
                      {eventContent}
                    </BodyText>
                  </GroupedEventContent>
                  {timeOnly && (
                    <GroupedEventTime>
                      <BodyText
                        color="var(--text-color-dim)"
                        size="x-small"
                        style={{ opacity: 0.8 }}
                      >
                        {timeOnly}
                      </BodyText>
                    </GroupedEventTime>
                  )}
                </GroupedEventItem>
              );
            })}
          </GroupedEventsContainer>
        </div>
      )}
    </>
  );
};

export default GroupedProfileEvent;
